import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminProtected from '../components/AdminProtected';
import { 
  MessageSquare, 
  Send, 
  Users, 
  Phone, 
  Loader2,
  CheckCircle,
  AlertCircle,
  History,
  School
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SMSMessagingPage = () => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [students, setStudents] = useState([]);
  const [studentsWithoutPhone, setStudentsWithoutPhone] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('announcement');
  const [customMessage, setCustomMessage] = useState('');
  const [sessionDate, setSessionDate] = useState('');
  const [sessionTopic, setSessionTopic] = useState('');
  const [smsHistory, setSmsHistory] = useState([]);
  const [schoolStats, setSchoolStats] = useState(null);

  // Fetch schools on mount
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/schools`);
        if (response.ok) {
          const data = await response.json();
          setSchools(data);
        }
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    };
    fetchSchools();
  }, []);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/sms/templates`);
        if (response.ok) {
          const data = await response.json();
          setTemplates(data.templates || []);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch SMS history on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/sms/history`);
        if (response.ok) {
          const data = await response.json();
          setSmsHistory(data.history || []);
        }
      } catch (error) {
        console.error('Error fetching SMS history:', error);
      }
    };
    fetchHistory();
  }, []);

  // Fetch students when school changes
  const fetchStudents = useCallback(async (schoolId) => {
    if (!schoolId) {
      setStudents([]);
      setStudentsWithoutPhone([]);
      setSchoolStats(null);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/sms/school/${schoolId}/students`);
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
        setStudentsWithoutPhone(data.students_without_phones || []);
        setSchoolStats({
          total: data.total_enrolled,
          withPhone: data.with_phone,
          withoutPhone: data.without_phone,
          schoolName: data.school_name
        });
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSchool) {
      fetchStudents(selectedSchool);
      setSelectedStudents([]);
    }
  }, [selectedSchool, fetchStudents]);

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handleStudentSelect = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSendSMS = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    if (selectedTemplate === 'announcement' && !customMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (selectedTemplate === 'session_reminder' && (!sessionDate || !sessionTopic)) {
      toast.error('Please enter session date and topic');
      return;
    }

    setSending(true);
    try {
      const templateData = {};
      if (selectedTemplate === 'session_reminder') {
        templateData.date = sessionDate;
        templateData.topic = sessionTopic;
      }

      const response = await fetch(`${BACKEND_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_ids: selectedStudents,
          school_id: selectedSchool,
          template_type: selectedTemplate,
          custom_message: customMessage,
          template_data: templateData
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message);
        setSelectedStudents([]);
        setCustomMessage('');
        // Refresh history
        const historyRes = await fetch(`${BACKEND_URL}/api/sms/history`);
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setSmsHistory(historyData.history || []);
        }
      } else {
        toast.error(data.detail || data.message || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminProtected title="SMS Messaging">
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1 pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                <MessageSquare className="text-blue-600" />
                SMS Messaging
              </h1>
              <p className="text-gray-600 mt-2">
                Send SMS messages to enrolled students
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Student Selection */}
              <div className="lg:col-span-2 space-y-6">
                {/* School Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <School className="text-blue-600" size={20} />
                      Select School
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedSchool} onValueChange={setSelectedSchool}>
                      <SelectTrigger data-testid="school-select">
                        <SelectValue placeholder="Choose a school" />
                      </SelectTrigger>
                      <SelectContent>
                        {schools.map(school => (
                          <SelectItem key={school.id} value={school.id}>
                            {school.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {schoolStats && (
                      <div className="mt-4 flex gap-4 flex-wrap">
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Users size={14} />
                          {schoolStats.total} Total Enrolled
                        </Badge>
                        <Badge variant="default" className="flex items-center gap-1 bg-green-600">
                          <Phone size={14} />
                          {schoolStats.withPhone} With Phone
                        </Badge>
                        {schoolStats.withoutPhone > 0 && (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <AlertCircle size={14} />
                            {schoolStats.withoutPhone} No Phone
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Student List */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Users className="text-green-600" size={20} />
                        Select Students
                      </CardTitle>
                      {students.length > 0 && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleSelectAll}
                          data-testid="select-all-btn"
                        >
                          {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                        </Button>
                      )}
                    </div>
                    <CardDescription>
                      {selectedStudents.length} of {students.length} students selected
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                      </div>
                    ) : students.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        {selectedSchool ? 'No students with phone numbers found' : 'Select a school to view students'}
                      </p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {students.map(student => (
                          <div 
                            key={student.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              selectedStudents.includes(student.id) 
                                ? 'bg-blue-50 border-blue-300' 
                                : 'bg-white hover:bg-gray-50'
                            }`}
                            onClick={() => handleStudentSelect(student.id)}
                            data-testid={`student-${student.id}`}
                          >
                            <Checkbox 
                              checked={selectedStudents.includes(student.id)}
                              onCheckedChange={() => handleStudentSelect(student.id)}
                            />
                            <div className="flex-1">
                              <p className="font-medium">{student.name}</p>
                              <p className="text-sm text-gray-500">{student.email}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-green-600 flex items-center gap-1">
                                <Phone size={12} />
                                {student.phone}
                              </p>
                              {student.branch && (
                                <p className="text-xs text-gray-400">{student.branch}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Message Composition */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Send className="text-purple-600" size={20} />
                      Compose Message
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Template Selection */}
                    <div className="space-y-2">
                      <Label>Message Type</Label>
                      <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                        <SelectTrigger data-testid="template-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {templates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Session Reminder Fields */}
                    {selectedTemplate === 'session_reminder' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="session-date">Session Date</Label>
                          <Input
                            id="session-date"
                            type="date"
                            value={sessionDate}
                            onChange={(e) => setSessionDate(e.target.value)}
                            data-testid="session-date-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="session-topic">Topic</Label>
                          <Input
                            id="session-topic"
                            value={sessionTopic}
                            onChange={(e) => setSessionTopic(e.target.value)}
                            placeholder="Enter session topic"
                            data-testid="session-topic-input"
                          />
                        </div>
                      </>
                    )}

                    {/* Custom Message */}
                    {selectedTemplate === 'announcement' && (
                      <div className="space-y-2">
                        <Label htmlFor="custom-message">Message</Label>
                        <Textarea
                          id="custom-message"
                          value={customMessage}
                          onChange={(e) => setCustomMessage(e.target.value)}
                          placeholder="Enter your message..."
                          rows={4}
                          maxLength={160}
                          data-testid="custom-message-input"
                        />
                        <p className="text-xs text-gray-500 text-right">
                          {customMessage.length}/160 characters
                        </p>
                      </div>
                    )}

                    {/* Preview */}
                    {selectedTemplate !== 'announcement' && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Message Preview:</p>
                        <p className="text-sm">
                          {templates.find(t => t.id === selectedTemplate)?.description || ''}
                        </p>
                      </div>
                    )}

                    {/* Send Button */}
                    <Button
                      onClick={handleSendSMS}
                      disabled={sending || selectedStudents.length === 0}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      data-testid="send-sms-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send size={16} className="mr-2" />
                      )}
                      Send to {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                    </Button>
                  </CardContent>
                </Card>

                {/* SMS History */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="text-orange-600" size={20} />
                      Recent Messages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {smsHistory.length === 0 ? (
                      <p className="text-gray-500 text-center py-4 text-sm">No messages sent yet</p>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {smsHistory.slice(0, 5).map((log, idx) => (
                          <div key={idx} className="border-b pb-2 last:border-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">{log.school_name}</p>
                              <Badge variant={log.sent_count > 0 ? "default" : "destructive"} className="text-xs">
                                {log.sent_count}/{log.recipient_count}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500 truncate">{log.message}</p>
                            <p className="text-xs text-gray-400">{log.sent_at_est}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </AdminProtected>
  );
};

export default SMSMessagingPage;
